import { useCallback, useEffect, useState } from "react";
import { adminService } from "../../features/admin/service";
import type {
  RealtimeOverviewResponse,
  RedisOverviewResponse,
  RedisNamespacesResponse,
  RedisClientsResponse,
  RedisQueuesResponse,
} from "../../features/admin/types";
import Spinner from "../../components/ui/Spinner";
import InfoTooltip from "../../components/common/InfoTooltip";
import RefreshButton from "../../components/admin/RefreshButton";

/**
 * Panels for Admin ▸ Realtime & Infrastructure.
 *
 * These exist to make three previously-invisible failures visible:
 *
 *  1. A worker whose cross-worker fan-out listener has died. It emits no
 *     error — it just stops receiving anything produced on another worker, so
 *     a fraction of users silently lose realtime until the process restarts.
 *  2. Redis connection exhaustion. N workers x 4 independent client pools can
 *     demand far more connections than a managed plan allows; every rejection
 *     is a request that could not reach Redis, and the only evidence is a
 *     counter nobody reads.
 *  3. Keys with no TTL under an allkeys-* eviction policy — the write-behind
 *     likes/saves buffer and the revoked-JWT list are both in that category,
 *     and both lose data rather than degrade when evicted.
 */

export const num = (n: number | null | undefined) =>
  n === null || n === undefined ? "—" : n.toLocaleString();

export function Card({ label, value, tone, hint }: { label: string; value: string; tone?: "bad" | "warn" | "ok"; hint?: string }) {
  const toneClass =
    tone === "bad" ? "text-red-400" : tone === "warn" ? "text-amber-400" : "text-slate-100";
  return (
    <div className="rounded-lg border border-white/10 bg-white/5 px-3 py-2">
      <p className="flex items-center text-[10px] uppercase tracking-wide text-slate-500">
        {label}
        {hint && <InfoTooltip text={hint} />}
      </p>
      <p className={`mt-0.5 text-lg font-semibold ${toneClass}`}>{value}</p>
    </div>
  );
}

export function Warnings({ items }: { items?: string[] }) {
  if (!items?.length) return null;
  return (
    <div className="mb-3 space-y-2">
      {items.map((w) => (
        <div
          key={w}
          role="alert"
          className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200"
        >
          {w}
        </div>
      ))}
    </div>
  );
}

/** Tab 1 — realtime workers and fan-out health. */
export function RealtimePanel() {
  const [data, setData] = useState<RealtimeOverviewResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setData(await adminService.getRealtimeOverview());
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load realtime overview");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  if (loading) return <Spinner />;
  if (err) return <p className="text-sm text-red-400">{err}</p>;
  if (!data) return null;

  const unhealthy = data.fanout_unhealthy_workers ?? [];

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <RefreshButton onRefresh={load} />
      </div>
      {unhealthy.length > 0 && (
        <Warnings
          items={[
            `${unhealthy.length} worker(s) have a STOPPED fan-out listener. They cannot ` +
              `receive events produced on other workers — users connected to them are ` +
              `silently missing realtime updates. Restart those processes.`,
          ]}
        />
      )}
      {data.degraded && <Warnings items={[data.degraded]} />}

      <div className="mb-4 grid grid-cols-2 gap-2 md:grid-cols-4">
        <Card label="Workers seen" value={num(data.worker_count)} />
        <Card label="DM sockets" value={num(data.total_dm_sockets)} />
        <Card label="Notification sockets" value={num(data.total_notification_sockets)} />
        <Card
          label="Fan-out unhealthy"
          value={num(unhealthy.length)}
          tone={unhealthy.length ? "bad" : "ok"}
        />
      </div>

      <div className="space-y-3">
        {data.workers.map((w) => (
          <div key={w.instance_id} className="rounded-lg border border-white/10 bg-white/5 p-3">
            <p className="mb-2 font-mono text-xs text-indigo-300">{w.instance_id}</p>
            <div className="mb-2 flex flex-wrap gap-3 text-xs text-slate-400">
              <span>DM: <b className="text-slate-200">{w.dm.sockets}</b> sockets / {w.dm.users} users</span>
              <span>Notifications: <b className="text-slate-200">{w.notifications.sockets}</b> sockets</span>
              <span>Active threads: {w.dm.active_threads}</span>
            </div>
            <div className="overflow-x-auto">
            <table className="w-full text-left text-[11px]">
              <thead className="text-slate-500">
                <tr>
                  <th className="py-1">channel</th><th>state</th><th>pub</th>
                  <th>recv</th><th>delivered</th><th>reconnects</th>
                </tr>
              </thead>
              <tbody>
                {w.fanout.map((f) => (
                  <tr key={f.name} className="border-t border-white/5">
                    {/* Every cell sets its own colour explicitly. Inheriting it
                        renders dark-on-dark and the value looks blank — which
                        is exactly how this table first shipped. */}
                    <td className="py-1 font-mono text-slate-300">{f.channel}</td>
                    <td className={f.running ? "text-emerald-400" : "text-red-400 font-bold"}>
                      {f.enabled ? (f.running ? "running" : "STOPPED") : "disabled"}
                    </td>
                    <td className="text-slate-300">{num(f.published)}</td>
                    <td className="text-slate-300">{num(f.received)}</td>
                    <td className="text-slate-300">{num(f.delivered)}</td>
                    <td className={f.reconnects > 0 ? "text-amber-400" : "text-slate-300"}>
                      {num(f.reconnects)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        ))}
      </div>
      {data.note && <p className="mt-3 text-[11px] text-slate-500">{data.note}</p>}
    </div>
  );
}

/** Tab 2 — Redis health, namespaces, connections. */
export function RedisPanel() {
  const [ov, setOv] = useState<RedisOverviewResponse | null>(null);
  const [ns, setNs] = useState<RedisNamespacesResponse | null>(null);
  const [cl, setCl] = useState<RedisClientsResponse | null>(null);
  const [match, setMatch] = useState("*");
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [o, c] = await Promise.all([
        adminService.getRedisOverview(),
        adminService.getRedisClients(),
      ]);
      setOv(o);
      setCl(c);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load Redis overview");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadNs = useCallback(async (pattern: string) => {
    try {
      setNs(await adminService.getRedisNamespaces(pattern));
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Namespace scan failed");
    }
  }, []);

  useEffect(() => {
    load();
    loadNs("*");
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load, loadNs]);

  if (loading) return <Spinner />;
  if (err) return <p className="text-sm text-red-400">{err}</p>;
  if (!ov?.enabled) return <p className="text-sm text-slate-400">Redis disabled: {ov?.reason}</p>;

  const m = ov.memory, c = ov.clients, st = ov.stats;
  const connPct = c?.maxclients ? (c.connected / c.maxclients) * 100 : 0;

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <RefreshButton onRefresh={async () => { await Promise.all([load(), loadNs(match)]); }} />
      </div>
      <Warnings items={ov.warnings} />

      <div className="mb-4 grid grid-cols-2 gap-2 md:grid-cols-4">
        <Card label="Memory used" value={m?.used_human ?? num(m?.used_bytes)} />
        <Card
          label="Connections"
          value={`${num(c?.connected)} / ${num(c?.maxclients)}`}
          tone={connPct > 70 ? "warn" : "ok"}
        />
        <Card
          label="Rejected connections"
          value={num(st?.rejected_connections)}
          tone={(st?.rejected_connections ?? 0) > 0 ? "bad" : "ok"}
        />
        <Card label="Ops/sec" value={num(st?.ops_per_sec)} />
        <Card
          label="Hit rate"
          value={st?.hit_rate_pct != null ? `${st.hit_rate_pct}%` : "—"}
          tone={(st?.hit_rate_pct ?? 100) < 50 ? "warn" : "ok"}
        />
        <Card label="Evicted keys" value={num(st?.evicted_keys)} tone={(st?.evicted_keys ?? 0) > 0 ? "warn" : "ok"} />
        <Card label="Expired keys" value={num(st?.expired_keys)} />
        <Card label="Eviction policy" value={m?.maxmemory_policy ?? "—"} />
      </div>

      <div className="mb-4">
        <div className="mb-2 flex items-center gap-2">
          <h4 className="text-sm font-semibold text-slate-200">Key namespaces</h4>
          <input
            value={match}
            onChange={(e) => setMatch(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") loadNs(match); }}
            placeholder="SCAN pattern, e.g. dm:*"
            className="rounded-md border border-white/10 bg-white/5 px-2 py-1 text-xs text-slate-200"
          />
          <button
            type="button"
            onClick={() => loadNs(match)}
            className="rounded-md border border-white/10 px-2 py-1 text-xs text-indigo-300 hover:bg-white/5"
          >
            Scan
          </button>
          {ns?.truncated && (
            <span className="text-[11px] text-amber-400">
              truncated at {num(ns.scanned)} keys
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead className="text-slate-500">
            <tr><th className="py-1">namespace</th><th>keys</th><th>without TTL</th></tr>
          </thead>
          <tbody>
            {(ns?.namespaces ?? []).slice(0, 25).map((r) => (
              <tr key={r.namespace} className="border-t border-white/5">
                <td className="py-1 font-mono text-slate-300">{r.namespace}</td>
                <td className="text-slate-300">{num(r.keys)}</td>
                <td className={r.keys_without_ttl > 0 ? "font-semibold text-amber-400" : "text-slate-500"}>
                  {num(r.keys_without_ttl)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <p className="mt-1 text-[11px] text-slate-500">
          Keys without a TTL are evictable under an <code>allkeys-*</code> policy. For the
          likes/saves write-behind buffer and the revoked-JWT list that means data loss, not
          slowdown.
        </p>
      </div>

      <div>
        <h4 className="mb-2 text-sm font-semibold text-slate-200">
          Live connections — {num(cl?.total)} total, {num(cl?.subscribers)} long-lived subscribers
        </h4>
        <p className="mb-2 text-[11px] text-slate-500">
          Each subscriber permanently holds one slot of its pool, reducing the connections
          available for ordinary commands on that worker.
        </p>
        <div className="max-h-72 overflow-x-auto overflow-y-auto">
          <table className="w-full text-left text-[11px]">
            <thead className="sticky top-0 bg-slate-900 text-slate-500">
              <tr><th className="py-1">addr</th><th>idle</th><th>cmd</th><th>what it's doing</th></tr>
            </thead>
            <tbody>
              {(cl?.clients ?? []).slice(0, 80).map((r, i) => (
                <tr key={`${r.addr}-${i}`} className="border-t border-white/5">
                  <td className="py-1 font-mono text-slate-400">{r.addr}</td>
                  <td className="text-slate-500">{r.idle_seconds}s</td>
                  <td className={r.is_subscriber ? "font-semibold text-indigo-300" : "text-slate-300"}>{r.cmd}</td>
                  <td className="text-slate-500">{r.cmd_explained}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/** Tab 3 — durable queues. */
export function QueuePanel() {
  const [q, setQ] = useState<RedisQueuesResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setQ(await adminService.getRedisQueues());
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load queues");
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  if (err) return <p className="text-sm text-red-400">{err}</p>;
  if (!q) return <Spinner />;

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <RefreshButton onRefresh={load} />
      </div>
      {q.note && (
        <div className="mb-3 rounded-lg border border-slate-500/30 bg-slate-500/10 px-3 py-2 text-xs text-slate-300">
          {q.note}
        </div>
      )}
      <div className="overflow-x-auto">
      <table className="w-full text-left text-xs">
        <thead className="text-slate-500">
          <tr><th className="py-1">stream</th><th>exists</th><th>length</th><th>groups</th></tr>
        </thead>
        <tbody>
          {q.queues.map((row) => (
            <tr key={row.name} className="border-t border-white/5">
              <td className="py-1 font-mono text-slate-300">{row.name}</td>
              <td className={row.exists ? "text-emerald-400" : "text-slate-500"}>
                {row.exists ? "yes" : "not created"}
              </td>
              <td className="text-slate-300">{num(row.length)}</td>
              <td className="text-slate-400">
                {(row.groups ?? []).length === 0
                  ? "—"
                  : (row.groups ?? [])
                      .map((g) => `${g.name}: ${g.consumers} consumers, ${g.pending} pending`)
                      .join(" · ")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}
